import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../config/api';

// ベースAPIクライアント
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

// リクエストインターセプターでJWTトークンを自動付与
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('subscnavi_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプターで401エラー時にトークンを削除
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('subscnavi_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ダッシュボードサマリーデータの型定義
export interface DashboardSummary {
  total_monthly_cost: number;
  total_subscriptions: number;
  next_payment_service: string;
  next_payment_date: string;
  next_payment_days: number;
  average_monthly_cost: number;
  total_annual_cost: number;
  subscriptions_by_cycle: {
    monthly: number;
    annual: number;
    semiannual: number;
  };
}

// サブスクリプションデータの型定義
export interface Subscription {
  id: number;
  user_id: number;
  service_name: string;
  price: number;
  cycle: 'monthly' | 'semiannual' | 'annual';
  initial_date: string;
  next_payment_date: string;
  payment_method?: string;
  category_id?: number;
  memo?: string;
  is_paused: boolean;
  paused_date?: string;
  created_at: string;
  updated_at?: string;
  category?: {
    id: number;
    name: string;
    user_id: number;
    created_at: string;
    updated_at?: string;
  };
}

// ダッシュボードサマリーを取得
export const fetchDashboardSummary = async (): Promise<DashboardSummary> => {
  const response = await apiClient.get('/dashboard/summary');
  return response.data;
};

// サブスクリプション一覧を取得
export const fetchSubscriptions = async (
  skip = 0,
  limit = 100,
  categoryId?: number
): Promise<Subscription[]> => {
  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString(),
  });
  
  if (categoryId) {
    params.append('category_id', categoryId.toString());
  }
  
  const response = await apiClient.get(`/subscriptions/?${params}`);
  return response.data;
};

// カテゴリ一覧を取得
export interface Category {
  id: number;
  name: string;
  user_id: number;
  created_at: string;
  updated_at?: string;
}

export const fetchCategories = async (): Promise<Category[]> => {
  const response = await apiClient.get('/subscriptions/categories');
  return response.data;
};

// サブスクリプション作成のリクエスト型
export interface CreateSubscriptionRequest {
  service_name: string;
  price: number;
  cycle: 'monthly' | 'semiannual' | 'annual';
  initial_date: string;
  next_payment_date?: string;
  payment_method?: string;
  category_id?: number;
  memo?: string;
}

// サブスクリプションを作成
export const createSubscription = async (
  subscriptionData: CreateSubscriptionRequest
): Promise<Subscription> => {
  const response = await apiClient.post('/subscriptions/', subscriptionData);
  return response.data;
};

// サブスクリプション更新のリクエスト型
export interface UpdateSubscriptionRequest {
  id: number;
  service_name?: string;
  price?: number;
  cycle?: 'monthly' | 'semiannual' | 'annual';
  initial_date?: string;
  next_payment_date?: string;
  payment_method?: string;
  category_id?: number;
  memo?: string;
}

// サブスクリプションを更新
export const updateSubscription = async (
  subscriptionData: UpdateSubscriptionRequest
): Promise<Subscription> => {
  const { id, ...updateData } = subscriptionData;
  const response = await apiClient.put(`/subscriptions/${id}`, updateData);
  return response.data;
};

// サブスクリプションを削除
export const deleteSubscription = async (id: number): Promise<void> => {
  await apiClient.delete(`/subscriptions/${id}`);
};

// サブスクリプションを一時停止
export const pauseSubscription = async (id: number): Promise<Subscription> => {
  const response = await apiClient.patch(`/subscriptions/${id}/pause`);
  return response.data;
};

// サブスクリプションを再開
export const resumeSubscription = async (id: number): Promise<Subscription> => {
  const response = await apiClient.patch(`/subscriptions/${id}/resume`);
  return response.data;
};
